<template>
  <v-container fluid class="pa-0">
    <v-row>
      <v-col class="pt-0 d-flex align-start" cols="12" sm="12" :md="showFilters ? 4 : 2" :class="{'mt-md-n10': !noMarginTop}">
        <v-card class="pb-2" elevation="0" outlined v-show="showFilters">
          <v-row class="mx-0 pt-2">
            <v-col class="py-0" cols="12" sm="12">
              <v-card-title class="pa-0 header-font py-2" style="font-size: 22px;">{{ $t('general.filters') }}</v-card-title>
              <MediaModeForm v-show="showFilters" v-if="(currentUser.clientType === 'Media' || currentUser.clientType === 'Driblab')" :fields="mediaModeFields"
                :mediaMode="mediaMode" @mediaModeChange="onMediaModeChange($event)"
                :mediaModeData="mediaModeData" @mediaModeDataChange="onMediaModeDataChange($event)" selectorMode></MediaModeForm>
            </v-col>
            <v-col class="d-flex py-1 mb-2 justify-center" cols="12" v-if="$vuetify.breakpoint.smAndDown">
              <v-btn color="primary" small outlined rounded @click="showShotOptions = !showShotOptions" class="elevation-0 monospace-font">
                {{ showShotOptions ? $t('game.detail.hideShotOptions') : $t('game.detail.showShotOptions')}}  <v-icon right > {{ showShotOptions ? 'keyboard_arrow_up' : 'keyboard_arrow_down'}}</v-icon>
              </v-btn>
            </v-col>
            <v-col class="py-0" cols="12" sm="12" v-if="showShotOptions">
              <!--Visualization-->
              <p class="my-0 pa-0 primary--text font-weight-bold body-1 mt-2 mb-1">{{ $t('general.orientation') }}</p>
              <v-radio-group v-model="orientationModel" dense hide-details row class="mt-0" @change="xaMap++">
                <v-radio :color="darkMode ? 'secondary darken-2' : 'secondary'" value="horizontal">
                  <template v-slot:label>
                    <span class="body-1 primary--text">{{ $t('general.horizontal') }}</span>
                  </template>
                </v-radio>
                <v-radio :color="darkMode ? 'secondary darken-2' : 'secondary'" value="vertical">
                  <template v-slot:label>
                    <span class="body-1 primary--text">{{ $t('general.vertical') }}</span>
                  </template>
                </v-radio>
              </v-radio-group>
              <p class="my-0 pa-0 primary--text font-weight-bold body-1 mt-2">{{ $t('finishingMap.areas') }}</p>
              <v-btn small @click="onSelectAreaClick()" rounded class="elevation-0 monospace-font white--text px-2 my-1" color="secondary">
                <v-icon color="white" x-small>filter_list</v-icon>
                <span class="ms-1">{{ `${$t('areaSelector.filterAreas')} (${areaModel.length}/${currentUserAreas.length})`  }}</span>
              </v-btn>
              <AreaSelectorModal 
                ref="areaSelectorModalRef" 
                v-if="game"
                :title="`${$t('playerDetail.keyPassMap')} - ${$t('areaSelector.selectAreas')}`"
                :areaModel="areaModel"
                :isVertical="true"
                @onConfirmClick="onAreaSelectorConfirm($event)"
              ></AreaSelectorModal>
              <p class="my-0 pa-0 primary--text font-weight-bold body-1 mt-2">{{ $t('finishingMap.typesOfShot') }}</p>
              <v-autocomplete
                :items="shotTypes"
                v-model="shotTypeModel"
                item-text="text"
                item-value="id"
                class="body-1 mb-4"
                multiple dense hide-details
                @input="updateShots();"
              >
                <v-list-item
                  slot="prepend-item"
                  ripple
                  @click="toggleShotTypes"
                >
                <v-list-item-content>
                  <v-list-item-title>{{ $t('finishingMap.selectAll') }}</v-list-item-title>
                </v-list-item-content>
              </v-list-item>
                <v-divider
                  slot="prepend-item"
                  class="mt-2"
                ></v-divider>
                <template
                  slot="selection"
                  slot-scope="{ item, index }"
                >
                  <v-chip v-if="index === 0" small color="lightSecondary lighten-2">
                    <span class="black--text">&nbsp;{{ item.text }}</span>
                  </v-chip>
                  <span
                    v-if="index === 1"
                    class="primary--text body-2 subheader-font"
                  >(+{{ shotTypeModel.length - 1 }} {{ $t('finishingMap.more') }})</span>
                </template>
                <template v-slot:item="{item}">
                  <span class="body-2 primary--text">{{item.text}}</span>
                </template>
              </v-autocomplete>
              <p class="my-0 pa-0 primary--text font-weight-bold body-1 mt-2">{{ $t('finishingMap.shotStrategy') }}</p>
              <v-autocomplete multiple hide-details dense clearable class="pa-0"
                v-model="shotStrategyModel"
                :items="shotStrategies"
                item-text="text"
                item-value="id"
                @change="onStrategyInput()"
              >
                <v-list-item slot="prepend-item" ripple @click="toggleShotStrategies">
                  <v-list-item-content>
                    <v-list-item-title>{{ $t('finishingMap.selectAll') }}</v-list-item-title>
                  </v-list-item-content>
                </v-list-item>
                <v-divider slot="prepend-item" class="mt-2"></v-divider>
                <template v-slot:selection="{item,index}">
                  <template v-if="!item.isGroupHeader">
                    <v-chip v-if="index === 0" small color="lightSecondary lighten-2" class="my-1">
                      <span class="black--text">&nbsp;{{ item.text }}</span>
                    </v-chip>
                    <span v-else-if="index === 1" class="primary--text body-2 subheader-font">(+{{ shotStrategyModel.filter(id => id !== 'group_open' && id !== 'group_set').length - 1 }} {{ $t('finishingMap.more') }})</span>
                  </template>
                </template>
                <template v-slot:item="{item}">
                  <div v-if="item.isGroupHeader" class="d-flex align-center" style="width:100%">
                    <v-icon small class="mr-2" color="secondary">{{ isStrategyGroupSelected(item.id) ? 'check_box' : 'check_box_outline_blank' }}</v-icon>
                    <span class="body-2 primary--text font-weight-bold">{{ item.text }}</span>
                  </div>
                  <span v-else class="body-2 primary--text">{{item.text}}</span>
                </template>
              </v-autocomplete>
              <p class="my-0 pa-0 primary--text font-weight-bold body-1 mt-2">{{ $t('finishingMap.bodyPart') }}</p>
              <v-autocomplete
                :items="bodyParts"
                v-model="bodyPartModel"
                item-text="text"
                item-value="id"
                class="body-1 mb-4"
                multiple dense hide-details
                @input="updateShots();"
              >
                <v-list-item
                  slot="prepend-item"
                  ripple
                  @click="toggleBodyPart"
                >
                <v-list-item-content>
                  <v-list-item-title>{{ $t('finishingMap.selectAll') }}</v-list-item-title>
                </v-list-item-content>
                </v-list-item>
                <v-divider
                  slot="prepend-item"
                  class="mt-2"
                ></v-divider>
                  <template
                    slot="selection"
                    slot-scope="{ item, index }"
                  >
                  <v-chip v-if="index === 0" small color="lightSecondary lighten-2">
                    <span class="black--text">&nbsp;{{ item.text }}</span>
                  </v-chip>
                  <span
                    v-if="index === 1"
                    class="primary--text body-2 subheader-font"
                  >(+{{ bodyPartModel.length - 1 }} {{ $t('finishingMap.more') }})</span>
                </template>
                <template v-slot:item="{item}">
                  <span class="body-2 primary--text">{{item.text}}</span>
                </template>
              </v-autocomplete>
            </v-col>
            <v-col class="py-1" cols="12" sm="12" v-if="showShotOptions">
              <p class="my-0 pa-0 primary--text font-weight-bold body-1 mt-2">{{ $t('game.gamePossessionMap.time') }}</p>
            </v-col>
            <v-col class="d-flex flex-wrap pt-0 pb-1" cols="12" v-if="showShotOptions">
              <v-checkbox
                v-model="periods"
                :label="$t('general.firstHalf')"
                color="secondary"
                value="period1"
                hide-details
                class="px-1 py-0 ma-0"
                @change="updateShots()"
              >
                <template v-slot:label>
                  <span class="body-1 primary--text">{{ $t('general.firstHalf') }}</span>
                </template>
              </v-checkbox>
              <v-checkbox
                v-model="periods"
                :label="$t('general.secondHalf')"
                color="secondary"
                value="period2"
                hide-details
                class="px-1 py-0 ma-0"
                @change="updateShots()"
              >
                <template v-slot:label>
                  <span class="body-1 primary--text">{{ $t('general.secondHalf') }}</span>
                </template>
              </v-checkbox>
              <v-checkbox
                v-if="hasFirstOT"
                v-model="periods"
                :label="$t('general.firstPeriodOT')"
                color="secondary"
                value="period3"
                hide-details
                class="px-1 py-0 ma-0"
                @change="updateShots()"
              >
                <template v-slot:label>
                  <span class="body-1 primary--text">{{ $t('general.firstPeriodOT') }}</span>
                </template>
              </v-checkbox>
              <v-checkbox
                v-if="hasSecondOT"
                v-model="periods"
                :label="$t('general.secondPeriodOT')"
                color="secondary"
                value="period4"
                hide-details
                class="px-1 py-0 ma-0"
                @change="updateShots()"
              >
                <template v-slot:label>
                  <span class="body-1 primary--text">{{ $t('general.secondPeriodOT') }}</span>
                </template>
              </v-checkbox>
            </v-col>
            <v-col class="d-flex pt-1 pb-2 mb-5" cols="12" sm="12" v-if="showShotOptions">
              <v-range-slider
                thumb-label
                v-model="minutes"
                :max="minutesMax"
                :min="minutesMin"
                step="1"
                hide-details
                :color="`secondary ${$vuetify.theme.dark ? 'lighten-3' : ''}`"
                class="align-center py-0"
                @input="updateShots"
              >
                <template v-slot:label>
                  <div class="d-flex flex-column">
                    <span class="primary--text font-weight-bold body-1">{{ $t('finishingMap.minutes') }}</span>
                    <span class="body-2 subheader-font monospace-font">{{ `(${minutesMin} - ${minutesMax})` }}</span>
                  </div>
                </template>
              </v-range-slider>
            </v-col>
          </v-row>
          <v-divider></v-divider>
          <v-row class="px-3">
            <v-col class="d-flex justify-end pt-2 pb-1 px-2" cols="12" >
              <v-btn
                color="secondary" class="elevation-0 monospace-font white--text px-3 mx-1" rounded small
                :x-small="!$vuetify.breakpoint.xsOnly"
                v-on:click.native="saveAsPNG()"
              >
                <v-icon small class="mr-1">
                  file_download</v-icon
                >
                <span class="">PNG</span>
              </v-btn>
              <v-btn
                color="secondary" class="elevation-0 monospace-font white--text px-3 mx-1" rounded small
                :x-small="!$vuetify.breakpoint.xsOnly"
                v-on:click.native="saveAsSVG()"
              >
                <v-icon small
                  >file_download</v-icon
                >
                <span>SVG</span>
              </v-btn>
              <v-btn
                color="secondary" class="elevation-0 monospace-font white--text px-3 mx-1" rounded small
                :x-small="!$vuetify.breakpoint.xsOnly"
                :loading="loadingExport"
                :disabled="!chartSize"
                v-on:click.native="onGenerateIFrameClick()"
              >
                <v-icon small class="mr-1"
                  >share</v-icon
                >
                <span>Embed</span>
              </v-btn>
            </v-col>
          </v-row>
        </v-card>
        <div class="flex-shrink-0 ml-2 align-self-start">
          <slot name="filters-toggle"></slot>
        </div>
      </v-col>
      <v-col cols="12" sm="12" :md="showFilters ? 8 : 10" id="chart" ref="chart" class="text-center pt-0">
        <GameAvatarMedia v-if="avatarSize && mediaMode && game" :width="avatarSize.width" :height="avatarSize.height" :id="`${gameId}-${chartId}`" :game="game"
          :margin="margin" 
          :titleLeft="isMobile ? game.homeTeamAlias : game.homeTeamName" 
          :titleRight="isMobile ? game.awayTeamAlias : game.awayTeamName"
          :leftColor="isWhite(mediaModeData.leftFillColor) ? mediaModeData.leftBorderColor : mediaModeData.leftFillColor"
          :rightColor="isWhite(mediaModeData.rightFillColor) ? mediaModeData.rightBorderColor : mediaModeData.rightFillColor"
          ref="gameAvatarMediaRef" :bgColor="mediaModeData.bgColor" :textColor="mediaModeData.textColor"
          type="keyPassMap" :lineDistance="mediaModeData.lineLength" :avatarSize="mediaModeData.avatarSize" :lineWeight="mediaModeData.lineWeight"
          :titleFontSize="mediaModeData.titleFontSize" :titleFontWeight="mediaModeData.titleFontWeight"
          :subtitle="mediaModeData.subtitle" :subtitleFontSize="mediaModeData.subtitleFontSize" :subtitleFontWeight="mediaModeData.subtitleFontWeight"
          :subtitle2="mediaModeData.subtitle2" :subtitle2FontSize="mediaModeData.subtitle2FontSize" :subtitle2FontWeight="mediaModeData.subtitle2FontWeight"
          :isVertical="orientationModel === 'vertical'">
        </GameAvatarMedia>
        <XAChart
          v-if="size != 0 && game !== null && shots.length > 0"
          :size="size"
          :type="type"
          :key="xaMap"
          :game="game"
          :passes="filteredShots"
          :language="currentUserLanguage"
          :homeFillColor="mediaModeData.leftFillColor"
          :awayFillColor="mediaModeData.rightFillColor"
          :homeBorderColor="mediaModeData.leftBorderColor"
          :awayBorderColor="mediaModeData.rightBorderColor"
          :homeTeamName="mediaModeData.titleLeft"
          :awayTeamName="mediaModeData.titleRight"
          :mediaMode="mediaMode"
          :id="`${gameId}-${chartId}`"
          :filterShotTypeText="filterShotTypeText"
          :filterShotStrategyText="filterShotStrategyText"
          :filterBodyPartText="filterBodyPartText"
          :filterTimeText="filterTimeText"
          :orientation="orientationModel"
          @sizeChange="onChartSizeChange($event)"
          @loaded="onLoaded($event)"
        />
        <DriblabSocialMedia v-if="game && avatarSize && size && mediaMode && mediaModeData.socialMedia"  :width="avatarSize.width" :height="50" :id="`${gameId}-${chartId}`"
          :margin="margin" ref="driblabSocialMediaRef" :bgColor="mediaModeData.bgColor" :textColor="mediaModeData.textColor"
          :fontSize="mediaModeData.socialMediaFontSize" :fontWeight="mediaModeData.socialMediaFontWeight"
          :logoCfg="mediaModeData.logo" :sponsor="mediaModeData.sponsor.dataUrl" type="finishingMap" 
          :isVertical="orientationModel === 'vertical'"
          :isGame="true">
        </DriblabSocialMedia>
      </v-col>
    </v-row>
    <ChartExportLinksModal ref="chartExportLinksModalRef" :title="`${game.homeTeamName} ${game.homeTeamScore} - ${game.awayTeamScore} ${game.awayTeamName}`" v-if="game"
    :subtitle="$t('playerDetail.keyPassMap')"></ChartExportLinksModal>
  </v-container>
</template>

<script>
import * as d3 from "d3";
import * as _ from "lodash";

import GameService from '../../services/game/game.service';
import XAChart from '../base/XAChart';
import MediaModeForm from '../base/MediaModeForm';
import DriblabSocialMedia from '../base/DriblabSocialMedia';
import GameAvatarMedia from './GameAvatarMedia';
import Dictionary from "../../dictionary";
import { exportSvg,downloadPng,copyAndAppendElement } from '../../utils/exportToPNG';
import { getNeutralColors, userHasRole,coordToArea } from '../../utils/functions';
import ChartExportLinksModal from '../base/ChartExportLinksModal';
import { v4 } from 'uuid';
import UserChartService from '../../services/user-chart.service';
import DocumentService from '../../services/document.service';
import AreaSelectorModal from "../modals/AreaSelectorModal.vue";
import UserService from '../../services/user.service';

const NODE_ENV = process.env.NODE_ENV;
const PROTOCOL = process.env.PROTOCOL || 'http://';
const END_POINT_URL = process.env.END_POINT_URL || window.location.hostname;
const END_POINT_PORT = process.env.END_POINT_PORT || '8081';

const API_URL = NODE_ENV === 'production' ? 'https://pro.driblab.com/embed-chart/' : PROTOCOL + END_POINT_URL + ':' + END_POINT_PORT + '/embed-chart/'

export default {
  name: 'GameXAMap',
  components: {
    XAChart,
    GameAvatarMedia,
    MediaModeForm,
    DriblabSocialMedia,
    ChartExportLinksModal,
    AreaSelectorModal
  },
  props:{
    noMarginTop: { type: Boolean, default: false },
    gameId:{
      type:Number,
      required: true
    },
    chartId:{
      type:Number,
      default: 0
    },
    language: {
      type: String,
      required: false
    },
    showFilters: {
      type: Boolean,
      default: true
    }
  },
  watch: {
    "mediaMode": function(newVal, oldVal) {
      if(!this.mediaMode){
        this.mediaModeData = {leftBorderColor:"#3366FF",rightBorderColor:"#3366FF",bgColor:"#ffffff0",socialMedia:true,textColor:"#000000",logo:"color",
                      titleFontSize:"18",titleFontWeight:"900",subtitleFontSize:"14",subtitleFontWeight:"500",lineWeight:"2",lineLength:"180",avatarSize:"64"
                      ,subtitle2FontSize:"12",subtitle2FontWeight:"300",sponsor:{}}
        this.mediaModeData.subtitle = this.game.leagueName;
        this.mediaModeData.subtitle2 = this.$t('playerDetail.keyPassMap');
        this.mediaModeData.titleLeft = this.game.homeTeamName;
        this.mediaModeData.titleRight = this.game.awayTeamName;
        this.mediaModeData.leftFillColor = this.game.homeTeamGameColor ? this.game.homeTeamGameColor.replace("0x","#") : getNeutralColors('home')[1];
        this.mediaModeData.rightFillColor = this.game.awayTeamGameColor ? this.game.awayTeamGameColor.replace("0x","#") : getNeutralColors('away')[1];
        this.mediaModeData.leftBorderColor = this.game.homeTeamGameSecondColor ? this.game.homeTeamGameSecondColor.replace("0x","#") : getNeutralColors('home')[0];
        this.mediaModeData.rightBorderColor = this.game.awayTeamGameSecondColor ? this.game.awayTeamGameSecondColor.replace("0x","#") : getNeutralColors('away')[0];
        this.mediaModeData.sponsor.src = require(`@/assets/img/driblab_normal_color.png`);
        this.mediaModeData.sponsor.fallBackSrc = require(`@/assets/img/driblab_normal_color.png`);
      }
      this.mediaModeData.bgColor = this.mediaMode ? '#ffffff' : '#ffffff0';
      this.onWindowResize(null,false)
    },
    showFilters() {
      this.$nextTick(() => {
        this.size = this.$refs.chart.clientWidth * this.sizeMultiplier;
        this.onWindowResize(null, true);
      });
    },
    orientationModel() {
      this.$nextTick(() => {
        this.onWindowResize(null, true);
      });
    },
  },
  computed: {
    currentUser() {
      return this.$store.state.auth.user;
    },
    currentUserLanguage() {
      return this.language ? this.language : this.$store.getters["auth/language"];
    },
    isMobile() {
      return this.$vuetify.breakpoint.smAndDown || this.$vuetify.breakpoint.mobile;
    },
    currentUserAreas(){
      let layoutId = this.$store.getters["auth/fieldLayout"]
      return _.chain(this.layouts).find(l => l.id === layoutId).get("areas").reduce((allAreas,a) => {
        allAreas.push(a);
        return allAreas;
      },[]).value();
    },
    layouts(){
      return this.$store.getters["general/fieldLayouts"];
    },
    hasFirstOT(){
      return _.some(this.shots,(s) => s.periodId === 3)
    },
    hasSecondOT(){
      return _.some(this.shots,(s) => s.periodId === 4)
    },
    avatarSize() {
      if (this.orientationModel === 'horizontal') {
        return {
          width: this.size,
          height: this.size / 6.77
        }
      }
      return {
        width: this.size / 1.49,
        height: this.size / 5
      }
    },
    lastSearchId() {
      return this.$store.state.auth.lastSearch;
    },
    darkMode(){
      return this.currentUser.darkMode || this.$vuetify.theme.dark;
    },
    allShotTypes () {
      return this.shotTypeModel.length === this.shotTypes.length;
    },
    allShotStrategies () {
      return this.shotStrategyModel.length === this.shotStrategies.filter(s => !s.isGroupHeader).length;
    },
    allBodyParts () {
      return this.bodyPartModel.length === this.bodyParts.length;
    },
    filterShotTypeText () {
      if (this.allShotTypes) return this.$t('general.all');
      const selected = this.shotTypes.filter(s => this.shotTypeModel.includes(s.id));
      if (!selected.length) return '—';
      if (selected.length === 1) return selected[0].text;
      return selected[0].text + ` +${selected.length - 1}`;
    },
    filterShotStrategyText () {
      if (this.allShotStrategies) return this.$t('general.all');
      const selected = this.shotStrategies.filter(s => !s.isGroupHeader && this.shotStrategyModel.includes(s.id));
      if (!selected.length) return '—';
      const selectedIds = selected.map(s => s.id);
      const allOpenPlay = this.openPlayIds.every(id => selectedIds.includes(id));
      const anySetPiece = this.setPieceIds.some(id => selectedIds.includes(id));
      const allSetPiece = this.setPieceIds.every(id => selectedIds.includes(id));
      const anyOpenPlay = this.openPlayIds.some(id => selectedIds.includes(id));
      if (allOpenPlay && !anySetPiece) return Dictionary[this.$i18n.locale]["finishingMap"]["openPlay"];
      if (allSetPiece && !anyOpenPlay) return Dictionary[this.$i18n.locale]["finishingMap"]["setPiece"];
      if (allOpenPlay && allSetPiece) return this.$t('general.all');
      if (selected.length === 1) return selected[0].text;
      return selected[0].text + ` +${selected.length - 1}`;
    },
    filterBodyPartText () {
      if (this.allBodyParts) return this.$t('general.all');
      const selected = this.bodyParts.filter(b => this.bodyPartModel.includes(b.id));
      if (!selected.length) return '—';
      if (selected.length === 1) return selected[0].text;
      return selected[0].text + ` +${selected.length - 1}`;
    },
    filterTimeText () {
      const customMinutes = this.minutes[0] !== this.minutesMin || this.minutes[1] !== this.minutesMax;
      if (customMinutes) {
        return `${this.minutes[0]}' - ${this.minutes[1]}'`;
      }
      const periodMap = {
        period1: this.$t('general.firstHalf'),
        period2: this.$t('general.secondHalf'),
        period3: this.$t('general.firstPeriodOT'),
        period4: this.$t('general.secondPeriodOT'),
      };
      const has1st = this.periods.includes('period1');
      const has2nd = this.periods.includes('period2');
      if (has1st && has2nd) return this.$t('videoEvents.fullGame') || 'Full Game';
      if (has1st && !has2nd) return periodMap['period1'];
      if (has2nd && !has1st) return periodMap['period2'];
      if (this.periods.length === 1) return periodMap[this.periods[0]];
      return periodMap[this.periods[0]] || 'Full Game';
    },
    sizeMultiplier () {
      if (this.orientationModel === 'vertical' && this.$vuetify.breakpoint.lgAndDown && !this.mediaMode) {
        return 1.25;
      } else return 0.925;
    }
  },
  data() {
    return {
      shots: [],
      filteredShots: [],
      size: 0,
      margin: null,
      type: 'game',
      game: null,
      xaMap: 0,
      showShotOptions: this.$vuetify.breakpoint.smAndDown ? false : true,
      shotTypes: "${player.position}" == "GK" ? [
        {id: 16, text: Dictionary[this.$i18n.locale]["finishingMap"]["goal"]},
        {id: 15, text: Dictionary[this.$i18n.locale]["finishingMap"]["savedShot"]}
      ] : [
        {id: 16, text: Dictionary[this.$i18n.locale]["finishingMap"]["goal"]},
        {id: 13, text: Dictionary[this.$i18n.locale]["finishingMap"]["missedShot"]},
        {id: 15, text: Dictionary[this.$i18n.locale]["finishingMap"]["savedShot"]},
        {id: 14, text: Dictionary[this.$i18n.locale]["finishingMap"]["shotOnPost"]},
        {id: 60, text: Dictionary[this.$i18n.locale]["finishingMap"]["missedChance"]}
      ],
      shotTypeModel: "${player.position}" == "GK" ? [15, 16] : [13, 14, 15, 16, 60],
      openPlayIds: [22, 23, 123, 215],
      setPieceIds: [26, 24, 25, 124, 160, 279],
      shotStrategies: [
        {id: 'group_open', text: Dictionary[this.$i18n.locale]["finishingMap"]["openPlay"], isGroupHeader: true},
        {id: 22, text: Dictionary[this.$i18n.locale]["finishingMap"]["regularPlay"]},
        {id: 23, text: Dictionary[this.$i18n.locale]["finishingMap"]["fastBreak"]},
        {id: 123, text: Dictionary[this.$i18n.locale]["game"]["gameThreat"]["keeperThrowPlay"]},
        {id: 215, text: Dictionary[this.$i18n.locale]["finishingMap"]["individualPlay"]},
        {id: 'group_set', text: Dictionary[this.$i18n.locale]["finishingMap"]["setPiece"], isGroupHeader: true},
        {id: 26, text: Dictionary[this.$i18n.locale]["finishingMap"]["freeKick"]},
        {id: 24, text: Dictionary[this.$i18n.locale]["finishingMap"]["indirectFreeKick"]},
        {id: 25, text: Dictionary[this.$i18n.locale]["finishingMap"]["fromCorner"]},
        {id: 124, text: Dictionary[this.$i18n.locale]["game"]["gameThreat"]["gkKick"]},
        {id: 160, text: Dictionary[this.$i18n.locale]["finishingMap"]["throwInSetPiece"]},
        {id: 279, text: Dictionary[this.$i18n.locale]["videoHighlights"]["qualifierIds"]["279"]},
      ],
      shotStrategyModel: [22, 23, 123, 215, 24, 25, 26, 124, 160, 279],
      areaModel: [],
      bodyParts: [
        {id: 15, text: Dictionary[this.$i18n.locale]["finishingMap"]["head"]},
        {id: 72, text: Dictionary[this.$i18n.locale]["finishingMap"]["leftFooted"]},
        {id: 20, text: Dictionary[this.$i18n.locale]["finishingMap"]["rightFooted"]},
        {id: 21, text: Dictionary[this.$i18n.locale]["finishingMap"]["otherBodyPart"]}
      ],
      bodyPartModel: [15, 20, 21, 72],
      minutes: [0, 90],
      periods: ['period1','period2', 'period3', 'period4'],
      minutesMax: 90,
      minutesMin: 0,
      mediaMode: false,
      mediaModeData: {leftBorderColor:"#3366FF",rightBorderColor:"#3366FF",bgColor:"#ffffff0",socialMedia:true,textColor:"#000000",logo:"color",
                      titleFontSize:"18",titleFontWeight:"900",subtitleFontSize:"14",subtitleFontWeight:"500",lineWeight:"2",lineLength:"180",avatarSize:"64"
                      ,subtitle2FontSize:"12",subtitle2FontWeight:"300",sponsor:{}},
      mediaModeFields: [
        {type:"color",label:"mediaMode.borderColorX",key:"leftBorderColor",labelParams:[1],home:true},
        {type:"color",label:"mediaMode.borderColorX",key:"rightBorderColor",labelParams:[2],away:true},
        {type:"color",label:"mediaMode.fillColorX",key:"leftFillColor",labelParams:[1],home:true},
        {type:"color",label:"mediaMode.fillColorX",key:"rightFillColor",labelParams:[2],away:true},
        {type:"text",label:"mediaMode.titleX",key:"titleLeft",labelParams:[1],home:true},
        {type:"text",label:"mediaMode.titleX",key:"titleRight",labelParams:[2],away:true},
        {type:"number",label:"mediaMode.titleFontSize",key:"titleFontSize",force:true},
        {type:"radio",label:"mediaMode.titleFontWeight",key:"titleFontWeight"
          ,options:[{value:"100",label:"100"},{value:"300",label:"300"},{value:"400",label:"400"},{value:"500",label:"500"},{value:"700",label:"700"},{value:"900",label:"900"}],force:true},
        {type:"text",label:"mediaMode.subtitle",key:"subtitle"},
        {type:"number",label:"mediaMode.subtitleFontSize",key:"subtitleFontSize"},
        {type:"radio",label:"mediaMode.subtitleFontWeight",key:"subtitleFontWeight"
          ,options:[{value:"100",label:"100"},{value:"300",label:"300"},{value:"400",label:"400"},{value:"500",label:"500"},{value:"700",label:"700"},{value:"900",label:"900"}]},
        {type:"text",label:"mediaMode.subtitleX",key:"subtitle2",labelParams:[2]},
        {type:"number",label:"mediaMode.subtitleFontSizeX",key:"subtitle2FontSize",labelParams:[2]},
        {type:"radio",label:"mediaMode.subtitleFontWeightX",key:"subtitle2FontWeight",labelParams:[2]
          ,options:[{value:"100",label:"100"},{value:"300",label:"300"},{value:"400",label:"400"},{value:"500",label:"500"},{value:"700",label:"700"},{value:"900",label:"900"}]},
        {type:"color",label:"mediaMode.textColor",key:"textColor"},
        {type:"color",label:"mediaMode.bgColor",key:"bgColor"},
        {type:"number",label:"mediaMode.avatarLineLength",key:"lineLength",force:true},
        {type:"number",label:"mediaMode.avatarLineWeight",key:"lineWeight",force:true},
        {type:"number",label:"mediaMode.avatarSize",key:"avatarSize",force:true},
        {type:"switch",label:"mediaMode.socialMedia",key:"socialMedia"},
        {type:"number",label:"mediaMode.socialMediaFontSize",key:"socialMediaFontSize",force:true},
        {type:"radio",label:"mediaMode.socialMediaFontWeight",key:"socialMediaFontWeight"
          ,options:[{value:"100",label:"100"},{value:"300",label:"300"},{value:"400",label:"400"},{value:"500",label:"500"},{value:"700",label:"700"},{value:"900",label:"900"}],force:true},
        {type:"image",label:"mediaMode.sponsor",key:"sponsor",force:true,aspectRatio:16/9,imageRestriction:'none',resizable:true,stencilSize:{width:300,height:300}}
      ],
      chartSize: null,
      loadingExport: false,
      orientationModel: 'horizontal',
      isEmbed: false
    };
  },
  mounted: function() {
    this.mediaMode = (this.currentUser.mediaMode || this.currentUser.clientType === 'Media');
    this.size = this.$refs.chart.clientWidth * this.sizeMultiplier; // minus padding
  },
  created() {
    window.addEventListener("resize", this.onWindowResize);
    return Promise.all([GameService.getXAPasses(this.gameId), GameService.getGame(this.gameId)]).then(([passesData, gameData]) => {
        this.shots = passesData.data;
        this.game = gameData.data;

        this.mediaModeData.subtitle = this.game.leagueName;
        this.mediaModeData.subtitle2 = this.$t('playerDetail.keyPassMap');
        this.mediaModeData.titleLeft = this.game.homeTeamName;
        this.mediaModeData.titleRight = this.game.awayTeamName;
        this.mediaModeData.leftFillColor = this.game.homeTeamGameColor ? this.game.homeTeamGameColor.replace("0x","#") : getNeutralColors('home')[1];
        this.mediaModeData.rightFillColor = this.game.awayTeamGameColor ? this.game.awayTeamGameColor.replace("0x","#") : getNeutralColors('away')[1];
        this.mediaModeData.leftBorderColor = this.game.homeTeamGameSecondColor ? this.game.homeTeamGameSecondColor.replace("0x","#") : getNeutralColors('home')[0];
        this.mediaModeData.rightBorderColor = this.game.awayTeamGameSecondColor ? this.game.awayTeamGameSecondColor.replace("0x","#") : getNeutralColors('away')[0];
        this.mediaModeData.sponsor.src = require(`@/assets/img/driblab_normal_color.png`);
        this.mediaModeData.sponsor.fallBackSrc = require(`@/assets/img/driblab_normal_color.png`);
        this.mediaModeFields = _.map(this.mediaModeFields,(f) => {
          if(f.home){
            f.labelParams = [this.game.homeTeamName];
          }
          if(f.away){
            f.labelParams = [this.game.awayTeamName];
          }
          return f;
        })
        this.$forceUpdate();

        this.setEventArea();

        this.updateMins();
        this.minutes = [this.minutesMin, this.minutesMax];
        this.updateShots();
        UserService.putLastSearchRendered({id:this.lastSearchId});
      });
  },
  methods: {
    onLoaded(event){
      //Emit when is loaded
      this.$emit("loaded", event);
    },
    onWindowResize(e,force=false) {
      if (this.size != this.$refs.chart.clientWidth * this.sizeMultiplier) {
        this.size = this.$refs.chart.clientWidth * this.sizeMultiplier;
      }
      this.xaMap += 1;
      if(this.$refs.gameAvatarMediaRef){
        this.$refs.gameAvatarMediaRef.printD3(force)
      }
      if(this.$refs.driblabSocialMediaRef){
        this.$refs.driblabSocialMediaRef.printD3(force)
      }
      this.$forceUpdate();
    },
    setEventArea(){
      this.shots = _.map(this.shots,(s) => {
        //Areas
        let startArea = coordToArea(s.x,s.y,this.currentUserAreas);
        s.startAreaId = _.get(startArea,'id');
        s.startAreaName = _.get(startArea,'name');
        let endArea = coordToArea(s.endX,s.endY,this.currentUserAreas);
        s.endAreaId = _.get(endArea,'id');
        s.endAreaName = _.get(endArea,'name');

        s.areaName = s.startAreaName;
        s.areaLabel = s.areaName;
        return s;
      })
    },
    shotsInArea(shots) {
      return shots
        .filter(s => this.areaModel.includes(s.startAreaId) || _.isEmpty(this.areaModel));
    },
    updateShots() {
      var self = this;

      var minutesMaxFilter = this.minutes[1];

      this.filteredShots = this.shots
      .filter(s => self.shotTypeModel.includes(s.eventTypeId))
      .filter(s => self.shotStrategyModel.includes(s.strategyId))
      .filter(s => self.bodyPartModel.includes(s.bodyPartId || 20))
      .filter(s => _.some(self.periods,p => p.includes(s.periodId)))
      .filter(s => s.min >= self.minutes[0] && s.min <= minutesMaxFilter);

      this.filteredShots = this.shotsInArea(this.filteredShots);

      let filteredShotsByxG = _.orderBy(this.filteredShots,['xG'],['desc']);
      this.$emit("filteredPasses", filteredShotsByxG);
      this.xaMap += 1;
    },
    toggleShotStrategies () {
      this.$nextTick(() => {
        if (this.allShotStrategies) {
          this.shotStrategyModel = []
        } else {
          this.shotStrategyModel = this.shotStrategies.filter(s => !s.isGroupHeader).map(s => s.id)
        }
        this.updateShots();
      })
    },
    onStrategyInput () {
      const hasGroupOpen = this.shotStrategyModel.includes('group_open');
      const hasGroupSet = this.shotStrategyModel.includes('group_set');
      if (hasGroupOpen || hasGroupSet) {
        let newModel = this.shotStrategyModel.filter(id => id !== 'group_open' && id !== 'group_set');
        if (hasGroupOpen) {
          const allSelected = this.openPlayIds.every(id => newModel.includes(id));
          if (allSelected) {
            newModel = newModel.filter(id => !this.openPlayIds.includes(id));
          } else {
            this.openPlayIds.forEach(id => { if (!newModel.includes(id)) newModel.push(id); });
          }
        }
        if (hasGroupSet) {
          const allSelected = this.setPieceIds.every(id => newModel.includes(id));
          if (allSelected) {
            newModel = newModel.filter(id => !this.setPieceIds.includes(id));
          } else {
            this.setPieceIds.forEach(id => { if (!newModel.includes(id)) newModel.push(id); });
          }
        }
        this.shotStrategyModel = newModel;
      }
      this.updateShots();
    },
    isStrategyGroupSelected (groupId) {
      const ids = groupId === 'group_open' ? this.openPlayIds : this.setPieceIds;
      return ids.every(id => this.shotStrategyModel.includes(id));
    },
    toggleShotTypes () {
      this.$nextTick(() => {
        if (this.allShotTypes) {
          this.shotTypeModel = []
        } else {
          this.shotTypeModel = this.shotTypes.map(function(p) { return p.id; })
        }
        this.updateShots();
      })
    },
    toggleBodyPart () {
      this.$nextTick(() => {
        if (this.allBodyParts) {
          this.bodyPartModel = []
        } else {
          this.bodyPartModel = this.bodyParts.map(function(p) { return p.id; })
        }
        this.updateShots();
      })
    },
    updateMins(){
      this.minutesMin = 0;
      this.minutesMax = _.chain(this.shots).map(s => s.min).max().value();
      if(this.minutesMax < 90){
        this.minutesMax = 90;
      }
      this.minutes = [this.minutesMin,this.minutesMax];
      this.$forceUpdate();
    },
    saveAsPNG() {
      if(this.mediaMode){
         downloadPng(this.prepareSvgForMedia(),`Driblab_XAMap_${this.game.homeTeamName.replace(' ','_')}_${this.game.homeTeamScore}_${this.game.awayTeamScore}_${this.game.awayTeamName.replace(' ','_')}.png`);
      }else{
        let svg = d3.select(`#pitchXA${this.gameId}-${this.chartId}`)
        .select("svg").node()
        downloadPng(svg,`Driblab_XAMap_${this.game.homeTeamName.replace(' ','_')}_${this.game.homeTeamScore}_${this.game.awayTeamScore}_${this.game.awayTeamName.replace(' ','_')}.png`);
      }
    },
    saveAsSVG() {
      if(this.mediaMode){
        exportSvg(this.prepareSvgForMedia(), `Driblab_XAMap_${this.game.homeTeamName.replace(' ','_')}_${this.game.homeTeamScore}_${this.game.awayTeamScore}_${this.game.awayTeamName.replace(' ','_')}`, true, this.mediaModeData.bgColor);
      }else{
        let svg = d3.select(`#pitchXA${this.gameId}-${this.chartId}`)
        .select("svg").node()
         exportSvg(svg, `Driblab_XAMap_${this.game.homeTeamName.replace(' ','_')}_${this.game.homeTeamScore}_${this.game.awayTeamScore}_${this.game.awayTeamName.replace(' ','_')}`);
      }
    },
    getElementToExport(){
      if(this.mediaMode){
        return this.prepareSvgForMedia();
      }else{
        let svg = d3.select(`#pitchXA${this.gameId}-${this.chartId}`)
          .select("svg").node()
       return svg;
      }
    },
    onMediaModeChange(event){
      this.mediaMode = event;
    },
    onMediaModeDataChange(event){
      this.mediaModeData = {...event};
      setTimeout(() => {this.onWindowResize(null,this.mediaModeData.force);},666)
    },
    prepareSvgForMedia(){
      let parent = document.createElementNS('http://www.w3.org/2000/svg','svg');
      let heightAccum = 0;
      let yOffset = 120;
      parent.style.background = this.mediaModeData.bgColor;


      //COPY HEADER
      copyAndAppendElement(parent,document.querySelector(`#gameAvatarMedia${this.gameId}-${this.chartId}SVG`),50,0)
      heightAccum += this.avatarSize.height;
      copyAndAppendElement(parent,document.querySelector(`#pitchXA${this.gameId}-${this.chartId}SVG`),50,this.avatarSize.height)
      heightAccum += this.chartSize.height;
      if(this.mediaModeData.socialMedia){
        copyAndAppendElement(parent,document.querySelector(`#driblabSocialMedia${this.gameId}-${this.chartId}SVG`),50,this.avatarSize.height + this.chartSize.height)
        heightAccum += 50;
      }

      parent.setAttribute("width",this.chartSize.width);
      parent.setAttribute("height",heightAccum);
      return parent;
    },
    onChartSizeChange(event){
      this.chartSize = event;
    },
    isWhite(color){
      return color === "#FFF" || color === "#fff" || color === "#FFFFFF" || color === "#ffffff" || color === "white";
    },
    async onGenerateIFrameClick(){
      this.isEmbed = true;
      this.loadingExport = true;
      let elToExport = this.getElementToExport();
      let key = v4();
      key = `${key}.json`;
      let chartObject = {
        awsKey:key,
        tool: 'key-pass-map',
        category: 'game',
        componentId: this.gameId,
        width: elToExport.getAttribute("width"),
        height: this.mediaMode ? elToExport.getAttribute("width") * 0.9925 : elToExport.getAttribute("width") * 0.806
      }
      let postResponse = await UserChartService.postChart(chartObject)
      let chartEntity = postResponse.data;

      this.$refs.chartExportLinksModalRef.open(chartEntity);
      this.loadingExport = false;

      let urlResponse = await UserChartService.getUploadURL(chartEntity.id,key);
      let uploadUrl = urlResponse.data;
      var jsonse = JSON.stringify({
        size: Number(chartEntity.width),
        height: Number(chartEntity.height),
        passes: this.filteredShots,
        circlesAndBoxes: this.checkboxCircles,
        language:this.currentUserLanguage,
        mediaModeData: this.mediaModeData,
        mediaMode: this.mediaMode,
        playerAvatarKey: 0,
        key: this.xaMap,
        socialMediaKey: 2,
        id: `${this.gameId}-${this.chartId}`,
        game:this.game,
        chartType:"key-pass-map",
        category: 'game',
        type:this.type,
        filterTimeText: this.filterTimeText,
        filterShotTypeText: this.filterShotTypeText,
        filterShotStrategyText: this.filterShotStrategyText,
        filterBodyPartText: this.filterBodyPartText,
        orientation: this.orientationModel,
        isEmbed: this.isEmbed,
        embedFactor: this.orientationModel === 'vertical' ? 0.67 : 1
      });

      var blob = new Blob([jsonse], {type: "application/json"});
      await DocumentService.upload(uploadUrl,blob);
      this.postSearchChart(chartEntity);
      this.isEmbed = false;
    },
    async postSearchChart(chart){
      const authorized = true;
      let metaInfo = null;
      let category = "game";
      let urlId = this.gameId;

      metaInfo = JSON.stringify({
        link: `${API_URL}${chart.id}/key/${chart.awsKey}/compId/${chart.componentId}/catId/${chart.categoryId}/tId/${chart.toolId}`
      });
      const tool = 'embed';
      const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      const browser = navigator.userAgent;
      let searchResponse = await UserService.postUserSearch(category, tool, urlId, authorized, this.$route.path, timeZone, browser, metaInfo);
      UserService.putLastSearchRendered({category,tool,id:searchResponse.data})
    },
    userHasRole(role) {
      return userHasRole(this.currentUser, role);
    },
    onSelectAreaClick(){
      this.$refs.areaSelectorModalRef.open();
    },
    onAreaSelectorConfirm(event){
      if (event && event.ids && event.ids.length > 0) this.areaModel = event.ids;
      else this.areaModel = [];
      this.updateShots();
    }
  },
  destroyed() {
    d3.selectAll('.tooltip').remove();
    window.removeEventListener("resize", this.onWindowResize);
  }
};
</script>

<style scoped>
  body > div.tooltip {
    font-family: 'NeueMontreal';
    pointer-events: none;
    position: absolute;
    background: none repeat scroll 0 0 #ffffff;
    padding: 5px 10px 5px 10px;
    border-radius: 4px;
    text-align: center;
    display: none;
    line-height: 1.5;
    color: #081F2C;
    box-shadow: 0px 3px 9px rgba(0, 0, 0, .15);
  }

  body > div.tooltip:after {
    box-sizing: border-box;
    display: block;
    width: 100%;
    line-height: 2.8;
    color: #ffffff;
    content: "\25BC";
    text-align: center;
  }

</style>
